import { auth, functions } from './firebase';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

// Session timeout duration: 30 minutes
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

// Minimum interval between server-side activity updates (5 minutes)
const SERVER_UPDATE_INTERVAL = 5 * 60 * 1000;

// Server-side session functions
const createSessionFn = httpsCallable(functions, 'createSession');
const updateSessionActivityFn = httpsCallable(functions, 'updateSessionActivity');
const deleteSessionFn = httpsCallable(functions, 'deleteSession');

// Warning duration: Show warning 2 minutes before timeout
const WARNING_DURATION = 2 * 60 * 1000; // 2 minutes in milliseconds

// Callback types
type TimeoutCallback = () => void;
type WarningCallback = () => void;

class SessionManager {
  private timeoutId: NodeJS.Timeout | null = null;
  private warningId: NodeJS.Timeout | null = null;
  private lastActivity: number = Date.now();
  private lastServerUpdate: number = 0;
  private isActive: boolean = false;
  private onTimeout: TimeoutCallback | null = null;
  private onWarning: WarningCallback | null = null;

  /**
   * Initialize the session manager
   */
  public start(onTimeout?: TimeoutCallback, onWarning?: WarningCallback): void {
    if (this.isActive) {
      return; // Already started
    }

    this.isActive = true;
    this.onTimeout = onTimeout || null;
    this.onWarning = onWarning || null;
    this.lastActivity = Date.now();

    // Start tracking user activity
    this.addActivityListeners();

    // Start timeout timers
    this.resetTimers();
  }

  /**
   * Stop the session manager
   */
  public stop(): void {
    this.isActive = false;
    this.clearTimers();
    this.removeActivityListeners();
  }

  /**
   * Reset the session timer (called when user is active)
   */
  public async resetTimer(): Promise<void> {
    if (!this.isActive) return;

    this.lastActivity = Date.now();
    this.resetTimers();

    // Throttle server-side updates to avoid flooding the network
    const now = Date.now();
    if (now - this.lastServerUpdate < SERVER_UPDATE_INTERVAL) return;
    this.lastServerUpdate = now;

    // Update server-side session activity
    try {
      await updateSessionActivityFn();
    } catch (error) {
      console.error('Failed to update session activity:', error);
      // If server rejects session, trigger logout
      if (error && typeof error === 'object' && 'code' in error) {
        if (error.code === 'unauthenticated') {
          await this.handleTimeout();
        }
      }
    }
  }

  /**
   * Get remaining time until timeout (in milliseconds)
   */
  public getRemainingTime(): number {
    if (!this.isActive) return 0;

    const elapsed = Date.now() - this.lastActivity;
    const remaining = SESSION_TIMEOUT - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Check if session is active
   */
  public isSessionActive(): boolean {
    return this.isActive;
  }

  /**
   * Manually trigger logout
   */
  public async logout(): Promise<void> {
    this.stop();
    try {
      // Delete server-side session
      await deleteSessionFn();
    } catch (error) {
      console.error('Failed to delete server session:', error);
    }

    try {
      await signOut(auth);
    } catch (err) {
      console.error('Session logout error:', err);
      throw err;
    }
  }

  /**
   * Create server-side session on login
   */
  public async createSession(): Promise<void> {
    try {
      await createSessionFn();
    } catch (error) {
      console.error('Failed to create server session:', error);
      throw error;
    }
  }

  // Private methods

  private resetTimers(): void {
    this.clearTimers();

    // Set warning timer (SESSION_TIMEOUT - WARNING_DURATION)
    const warningTime = SESSION_TIMEOUT - WARNING_DURATION;
    this.warningId = setTimeout(() => {
      if (this.onWarning) {
        this.onWarning();
      }
    }, warningTime);

    // Set timeout timer
    this.timeoutId = setTimeout(() => {
      this.handleTimeout();
    }, SESSION_TIMEOUT);
  }

  private clearTimers(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningId) {
      clearTimeout(this.warningId);
      this.warningId = null;
    }
  }

  private async handleTimeout(): Promise<void> {
    if (this.onTimeout) {
      this.onTimeout();
    }

    // Automatically logout
    await this.logout();
  }

  private activityHandler = async (): Promise<void> => {
    await this.resetTimer();
  };

  private addActivityListeners(): void {
    // Listen for user activity events
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    events.forEach((event) => {
      document.addEventListener(event, this.activityHandler, { passive: true });
    });
  }

  private removeActivityListeners(): void {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    events.forEach((event) => {
      document.removeEventListener(event, this.activityHandler);
    });
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();

// Export utility functions
export const startSessionTimeout = (
  onTimeout?: TimeoutCallback,
  onWarning?: WarningCallback
): void => {
  sessionManager.start(onTimeout, onWarning);
};

export const stopSessionTimeout = (): void => {
  sessionManager.stop();
};

export const resetSessionTimeout = (): void => {
  sessionManager.resetTimer();
};

export const getRemainingSessionTime = (): number => {
  return sessionManager.getRemainingTime();
};

export const formatRemainingTime = (ms: number): string => {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};
